import { useState, useEffect, useCallback } from 'react';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { request } from '../../../shared/services/api';

interface SolicitudCompra {
  id: string;
  codigo: string;
  descripcion: string;
  estado: string;
  importeEstimado: number;
  moneda: string;
  prioridad: string;
  solicitante: { id: string; nombreCompleto: string; email: string };
  aprobador: { id: string; nombreCompleto: string; email: string } | null;
  proyecto: { id: string; codigo: string; nombre: string } | null;
  centro: { id: string; codigo: string; nombre: string } | null;
  fechaAprobacion: string | null;
  creadoEn: string;
  actualizadoEn: string;
  pedidos?: Array<{
    id: string;
    codigo: string;
    estado: string;
    nombreProveedor: string;
    importe: number;
    moneda: string;
  }>;
}

const ESTADOS_FILTRO = [
  { value: '', label: 'Todas' },
  { value: 'PENDIENTE_APROBACION', label: 'Pendiente' },
  { value: 'APROBADA', label: 'Aprobada' },
  { value: 'RECHAZADA', label: 'Rechazada' },
  { value: 'BORRADOR', label: 'Borrador' },
];

const ESTADO_COLORS: Record<string, { color: string; bg: string }> = {
  PENDIENTE_APROBACION: { color: '#C05621', bg: '#FEEBC8' },
  APROBADA: { color: '#276749', bg: '#C6F6D5' },
  PEDIDA: { color: '#2B6CB0', bg: '#BEE3F8' },
  CERRADA: { color: '#276749', bg: '#C6F6D5' },
  RECHAZADA: { color: '#9B2C2C', bg: '#FED7D7' },
  BORRADOR: { color: '#718096', bg: '#EDF2F7' },
  CANCELADA: { color: '#4A5568', bg: '#E2E8F0' },
};

function tiempoRelativo(fecha: string): string {
  const diff = Date.now() - new Date(fecha).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Ahora';
  if (min < 60) return `Hace ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Hace ${h}h`;
  return `Hace ${Math.floor(h / 24)}d`;
}

function formatImporte(n: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);
}

export function ComprasPage() {
  const [items, setItems] = useState<SolicitudCompra[]>([]);
  const [selected, setSelected] = useState<SolicitudCompra | null>(null);
  const [filtro, setFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filtro ? `?estado=${filtro}` : '';
      const res = await request<{ data: SolicitudCompra[]; meta: { total: number } }>(
        `/apps/compras/solicitudes${qs}`,
      );
      setItems(res.data);
    } catch {
      /* silencioso */
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  const fetchDetail = useCallback(async (id: string) => {
    try {
      const res = await request<{ data: SolicitudCompra }>(`/apps/compras/solicitudes/${id}`);
      setSelected(res.data);
    } catch {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const doAction = async (id: string, action: 'enviar' | 'aprobar' | 'rechazar') => {
    if (acting) return;
    let body: Record<string, unknown> = {};
    if (action === 'rechazar') {
      const motivo = window.prompt('Motivo del rechazo:');
      if (!motivo) return;
      body = { comentarios: motivo };
    }
    setActing(true);
    try {
      await request(`/apps/compras/solicitudes/${id}/${action}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      await fetchList();
      await fetchDetail(id);
    } catch {
      /* silencioso */
    } finally {
      setActing(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Panel izquierdo — lista */}
      <div
        style={{
          width: 300,
          flexShrink: 0,
          borderRight: '1px solid #E8E8E8',
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
        }}
      >
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #F0F0F0' }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1F3864', margin: '0 0 12px' }}>
            Solicitudes de compra
          </h2>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ESTADOS_FILTRO.map((e) => (
              <button
                key={e.value}
                onClick={() => setFiltro(e.value)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 14,
                  fontSize: 11,
                  fontWeight: filtro === e.value ? 600 : 400,
                  color: filtro === e.value ? '#fff' : '#4A5568',
                  background: filtro === e.value ? '#1F3864' : '#F7F7F7',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {e.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <LoadingSpinner size="sm" muted />
          ) : items.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#CBD5E0"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ display: 'block', margin: '0 auto 12px' }}
              >
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              <p style={{ color: '#A0AEC0', fontSize: 13, margin: 0 }}>Sin solicitudes</p>
            </div>
          ) : (
            items.map((sc) => {
              const ec = ESTADO_COLORS[sc.estado] ?? ESTADO_COLORS.BORRADOR;
              const isActive = selected?.id === sc.id;
              return (
                <div
                  key={sc.id}
                  onClick={() => fetchDetail(sc.id)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #F5F5F5',
                    cursor: 'pointer',
                    background: isActive ? '#EBF5FF' : '#fff',
                    borderLeft: isActive ? '3px solid #1F3864' : '3px solid transparent',
                    transition: 'background 0.1s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontWeight: 700,
                        fontSize: 13,
                        color: '#1F3864',
                      }}
                    >
                      {sc.codigo}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 4,
                        fontWeight: 600,
                        color: ec.color,
                        background: ec.bg,
                      }}
                    >
                      {sc.estado.replace('_', ' ')}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: '#4A5568',
                      marginTop: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sc.descripcion.substring(0, 60)}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ fontSize: 12, color: '#33BCE1', fontWeight: 600 }}>
                      {formatImporte(sc.importeEstimado)}
                    </span>
                    <span style={{ fontSize: 11, color: '#A0AEC0' }}>
                      {tiempoRelativo(sc.creadoEn)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Panel derecho — detalle */}
      <div style={{ flex: 1, overflow: 'auto', background: '#F8F8F8' }}>
        {!selected ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#CBD5E0"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ display: 'block', margin: '0 auto 12px' }}
              >
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              <p style={{ color: '#A0AEC0', fontSize: 14 }}>Selecciona una solicitud</p>
            </div>
          </div>
        ) : (
          <div style={{ padding: 24, maxWidth: 700 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontWeight: 700,
                  fontSize: 18,
                  color: '#1F3864',
                }}
              >
                {selected.codigo}
              </span>
              {(() => {
                const ec = ESTADO_COLORS[selected.estado] ?? ESTADO_COLORS.BORRADOR;
                return (
                  <span
                    style={{
                      fontSize: 12,
                      padding: '2px 10px',
                      borderRadius: 6,
                      fontWeight: 600,
                      color: ec.color,
                      background: ec.bg,
                    }}
                  >
                    {selected.estado.replace('_', ' ')}
                  </span>
                );
              })()}
              <button
                onClick={() => setSelected(null)}
                style={{
                  marginLeft: 'auto',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#A0AEC0',
                  fontSize: 18,
                }}
              >
                ✕
              </button>
            </div>
            <p style={{ fontSize: 14, color: '#4A5568', margin: '0 0 16px', lineHeight: 1.5 }}>
              {selected.descripcion}
            </p>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#33BCE1', marginBottom: 20 }}>
              {formatImporte(selected.importeEstimado)}
            </div>

            {/* Grid de datos */}
            <div
              style={{
                background: '#fff',
                borderRadius: 10,
                padding: 20,
                marginBottom: 16,
                border: '1px solid #E8E8E8',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px 24px',
                fontSize: 13,
              }}
            >
              <div>
                <div style={{ color: '#718096', fontSize: 11, marginBottom: 2 }}>Solicitante</div>
                <div style={{ color: '#1A202C', fontWeight: 500 }}>
                  {selected.solicitante.nombreCompleto}
                </div>
              </div>
              <div>
                <div style={{ color: '#718096', fontSize: 11, marginBottom: 2 }}>Prioridad</div>
                <div style={{ color: '#1A202C', fontWeight: 500 }}>{selected.prioridad}</div>
              </div>
              {selected.proyecto && (
                <div>
                  <div style={{ color: '#718096', fontSize: 11, marginBottom: 2 }}>Proyecto</div>
                  <div style={{ color: '#1A202C', fontWeight: 500 }}>
                    {selected.proyecto.codigo} — {selected.proyecto.nombre}
                  </div>
                </div>
              )}
              {selected.centro && (
                <div>
                  <div style={{ color: '#718096', fontSize: 11, marginBottom: 2 }}>Centro</div>
                  <div style={{ color: '#1A202C', fontWeight: 500 }}>
                    {selected.centro.codigo} — {selected.centro.nombre}
                  </div>
                </div>
              )}
              {selected.aprobador && (
                <div>
                  <div style={{ color: '#718096', fontSize: 11, marginBottom: 2 }}>Aprobador</div>
                  <div style={{ color: '#1A202C', fontWeight: 500 }}>
                    {selected.aprobador.nombreCompleto}
                  </div>
                </div>
              )}
              {selected.fechaAprobacion && (
                <div>
                  <div style={{ color: '#718096', fontSize: 11, marginBottom: 2 }}>
                    Fecha aprobación
                  </div>
                  <div style={{ color: '#1A202C', fontWeight: 500 }}>
                    {new Date(selected.fechaAprobacion).toLocaleDateString('es-ES')}
                  </div>
                </div>
              )}
            </div>

            {/* Pedidos */}
            {selected.pedidos && selected.pedidos.length > 0 && (
              <div
                style={{
                  background: '#fff',
                  borderRadius: 10,
                  padding: 20,
                  marginBottom: 16,
                  border: '1px solid #E8E8E8',
                }}
              >
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1F3864', margin: '0 0 12px' }}>
                  Pedidos ({selected.pedidos.length})
                </h3>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E8E8E8' }}>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '6px 8px',
                          color: '#718096',
                          fontWeight: 600,
                        }}
                      >
                        Código
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '6px 8px',
                          color: '#718096',
                          fontWeight: 600,
                        }}
                      >
                        Proveedor
                      </th>
                      <th
                        style={{
                          textAlign: 'right',
                          padding: '6px 8px',
                          color: '#718096',
                          fontWeight: 600,
                        }}
                      >
                        Importe
                      </th>
                      <th
                        style={{
                          textAlign: 'center',
                          padding: '6px 8px',
                          color: '#718096',
                          fontWeight: 600,
                        }}
                      >
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.pedidos.map((ped) => (
                      <tr key={ped.id} style={{ borderBottom: '1px solid #F5F5F5' }}>
                        <td
                          style={{
                            padding: '8px',
                            fontFamily: 'JetBrains Mono, monospace',
                            fontWeight: 600,
                          }}
                        >
                          {ped.codigo}
                        </td>
                        <td style={{ padding: '8px' }}>{ped.nombreProveedor}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                          {formatImporte(ped.importe)}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <span
                            style={{
                              fontSize: 10,
                              padding: '1px 6px',
                              borderRadius: 4,
                              fontWeight: 600,
                              ...(ESTADO_COLORS[ped.estado] ?? ESTADO_COLORS.BORRADOR),
                            }}
                          >
                            {ped.estado}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Acciones */}
            {(selected.estado === 'BORRADOR' || selected.estado === 'PENDIENTE_APROBACION') && (
              <div style={{ display: 'flex', gap: 10 }}>
                {selected.estado === 'BORRADOR' && (
                  <button
                    disabled={acting}
                    onClick={() => doAction(selected.id, 'enviar')}
                    style={{
                      padding: '10px 24px',
                      borderRadius: 8,
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 14,
                      fontFamily: 'inherit',
                      background: '#1F3864',
                      color: '#fff',
                    }}
                  >
                    Enviar a aprobación
                  </button>
                )}
                {selected.estado === 'PENDIENTE_APROBACION' && (
                  <>
                    <button
                      disabled={acting}
                      onClick={() => doAction(selected.id, 'aprobar')}
                      style={{
                        padding: '10px 24px',
                        borderRadius: 8,
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 14,
                        fontFamily: 'inherit',
                        background: '#38A169',
                        color: '#fff',
                      }}
                    >
                      Aprobar
                    </button>
                    <button
                      disabled={acting}
                      onClick={() => doAction(selected.id, 'rechazar')}
                      style={{
                        padding: '10px 24px',
                        borderRadius: 8,
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 14,
                        fontFamily: 'inherit',
                        background: '#E53E3E',
                        color: '#fff',
                      }}
                    >
                      Rechazar
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
